import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { BrowserManagerDataService } from '../../../browser-manager-data/browser-manager-data.service';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { CurrentPlan } from '../../../features-data/sub-models/current-plan.model';
import { FeedbackDataDispatcher } from '../../../feedback-data/feedback-data.dispatcher';
import { LocalizationService } from '../../../localization/localization.service';
import { Settings } from '../../../settings-data/settings.model';
import { FeedbackDialogBuilder } from './feedback-dialog.builder';
import { filter, takeUntil } from 'rxjs/operators';
import { FeedbackScreenEnum } from '../../../feedback-data/feedback-data.data';
import {
  ITrackingProperties,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { FeedbackTrackingObject } from '../../../tracking/models/objects/feedback-tracking-object.model';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';

export const DIALOG_TYPE = 'feedback-dialog';

@Component({
  selector: 'app-feedback-dialog',
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [FeedbackDialogBuilder],
})
export class FeedbackDialogComponent implements OnInit, OnDestroy {
  @Input() feedbackScreenName: FeedbackScreenEnum;
  @Input() userEmail: string;
  @Input() settings: Settings;
  @Input() currentPlan: CurrentPlan;
  @Input() accountId: string;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: FeedbackTrackingObject;

  private _destroyed$: Subject<boolean> = new Subject<boolean>();
  private _defaultTags: string[];

  dialogType: string;
  descriptionFormControl: AbstractControl;
  trackingAdditionalProperties: ITrackingProperties;

  constructor(
    private _feedbackDataDispatcher: FeedbackDataDispatcher,
    private _dialogService: DialogService,
    private _localizationService: LocalizationService,
    private _browserManagerDataService: BrowserManagerDataService,
    private _feedbackDialogBuilder: FeedbackDialogBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this._defaultTags = this._feedbackDialogBuilder.buildDefaultTags(
      this._browserManagerDataService.getDevice(),
      this.currentPlan,
      this.userEmail,
      this._localizationService.getBrowserCultureLang(),
      this.settings,
      this.accountId
    );

    this.dialogType = DIALOG_TYPE;
    this.descriptionFormControl = new FormControl(null);

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ action, type }: IDialogTrigger) =>
            action === DialogActionsEnum.open && type === DIALOG_TYPE
        ),
        takeUntil(this._destroyed$)
      )
      .subscribe(() => this.descriptionFormControl.reset(''));

    this.trackingAdditionalProperties = {
      type: 'dialog',
    };
  }

  sendFeedback() {
    const trackingAction = this._trackingActionBuilder.buildButtonTapped(
      'send',
      this.trackingScreenName
    );
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      trackingAction,
      this.trackingAdditionalProperties
    );

    this._feedbackDataDispatcher.sendFeedback200({
      screen: this.feedbackScreenName,
      screenProperties: this._defaultTags.join(','),
      comment: this.descriptionFormControl.value,
      userEmail: this.userEmail,
    });
  }

  onDismiss() {
    const trackingAction = this._trackingActionBuilder.buildButtonTapped(
      'dismiss',
      this.trackingScreenName
    );
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      trackingAction,
      this.trackingAdditionalProperties
    );
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.unsubscribe();
  }
}
